import React, { useEffect, useMemo, useState } from "react";
import { Pencil, AlertTriangle, Loader2, ArrowRight, ChevronDown } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "src/shadcn/ui/dialog";
import { EuRelabelCheckInProductDateType } from "src/types";

const DATE_TYPE_OPTIONS: EuRelabelCheckInProductDateType[] = ["BBE", "Use By", "Best By", "MFG"];

export interface EditCaseProductModalSubmit {
  newQuantity: number;
  productDate: string;
  dateType: EuRelabelCheckInProductDateType | "";
  lotNumber: string;
}

interface EditCaseProductModalProps {
  open: boolean;
  onClose: () => void;
  productSkuName?: string;
  productName?: string;
  caseId: string;
  currentQty: number;
  /** received - distributed-on-other-cases — i.e. the maximum newQuantity the user can pick. */
  maxAllowedQty: number;
  initialProductDate?: string;
  initialDateType?: EuRelabelCheckInProductDateType | "";
  initialLotNumber?: string;
  isSubmitting?: boolean;
  submitError?: string;
  onSubmit: (input: EditCaseProductModalSubmit) => Promise<void> | void;
}

const EditCaseProductModal: React.FC<EditCaseProductModalProps> = ({
  open,
  onClose,
  productSkuName,
  productName,
  caseId,
  currentQty,
  maxAllowedQty,
  initialProductDate = "",
  initialDateType = "",
  initialLotNumber = "",
  isSubmitting = false,
  submitError,
  onSubmit
}) => {
  const [newQty, setNewQty] = useState<string>(String(currentQty));
  const [productDate, setProductDate] = useState<string>(initialProductDate);
  const [dateType, setDateType] = useState<EuRelabelCheckInProductDateType | "">(initialDateType);
  const [lotNumber, setLotNumber] = useState<string>(initialLotNumber);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setNewQty(String(currentQty));
      setProductDate(initialProductDate);
      setDateType(initialDateType);
      setLotNumber(initialLotNumber);
      setTouched(false);
    }
  }, [open, currentQty, initialProductDate, initialDateType, initialLotNumber]);

  const parsedQty = Number(newQty);

  const qtyError = useMemo(() => {
    if (newQty.trim() === "") return "Enter a new quantity";
    if (!Number.isInteger(parsedQty) || parsedQty < 1) {
      return "Quantity must be a whole number ≥ 1";
    }
    if (parsedQty > maxAllowedQty) {
      return `Only ${maxAllowedQty} unit${maxAllowedQty !== 1 ? "s" : ""} available`;
    }
    return undefined;
  }, [newQty, parsedQty, maxAllowedQty]);

  const trimmedDate = productDate.trim();
  const hasDate = trimmedDate !== "";
  const hasType = dateType !== "";
  const datePairError = useMemo(() => {
    if (hasDate && !hasType) return "Pick a date type";
    if (!hasDate && hasType) return "Pick a date";
    return undefined;
  }, [hasDate, hasType]);

  const trimmedLot = lotNumber.trim();

  const isDirty =
    parsedQty !== currentQty ||
    trimmedDate !== initialProductDate ||
    dateType !== initialDateType ||
    trimmedLot !== initialLotNumber;

  const canSubmit = !qtyError && !datePairError && isDirty && !isSubmitting;

  const handleSubmit = async () => {
    setTouched(true);
    if (!canSubmit) return;
    await onSubmit({
      newQuantity: parsedQty,
      productDate: trimmedDate,
      dateType,
      lotNumber: trimmedLot
    });
  };

  const delta = parsedQty - currentQty;
  const showDelta = !qtyError && delta !== 0;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isSubmitting && onClose()}>
      <DialogContent className="max-w-[480px] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-secondary-100">
          <DialogTitle className="flex items-center gap-2.5 text-base font-semibold text-secondary-900">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-secondary-900 text-white">
              <Pencil className="w-3.5 h-3.5" />
            </span>
            Edit Case Product
          </DialogTitle>
          <DialogDescription className="text-xs text-secondary-500 mt-1.5 leading-relaxed">
            Adjust the quantity, date, or lot for this product on case{" "}
            <span className="font-mono font-semibold text-secondary-700">{caseId}</span>. Labels will regenerate after
            saving.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5 space-y-5">
          {(productSkuName || productName) && (
            <div className="rounded-lg border border-secondary-200 bg-secondary-50/50 px-3.5 py-3">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-secondary-400">SKU</span>
                {productSkuName && (
                  <span className="font-mono text-[11px] font-bold text-secondary-900 bg-white border border-secondary-200 rounded px-1.5 py-0.5">
                    {productSkuName}
                  </span>
                )}
              </div>
              {productName && <p className="text-sm text-secondary-700 leading-snug break-words">{productName}</p>}
            </div>
          )}

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-secondary-500 mb-2">
              Quantity
            </label>
            <div className="flex items-center gap-3">
              <div className="flex-1 rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2.5">
                <p className="text-[10px] font-medium uppercase tracking-wider text-secondary-400 leading-none">
                  Current
                </p>
                <p className="mt-1 text-base font-semibold text-secondary-700 tabular-nums leading-none">
                  {currentQty}
                </p>
              </div>
              <ArrowRight className="w-4 h-4 text-secondary-300 flex-shrink-0" />
              <div className="flex-1 rounded-lg border border-secondary-300 bg-white px-3 py-2.5 focus-within:border-secondary-900 focus-within:ring-1 focus-within:ring-secondary-900 transition-colors">
                <label
                  htmlFor="simple-edit-qty"
                  className="block text-[10px] font-medium uppercase tracking-wider text-secondary-400 leading-none"
                >
                  New
                </label>
                <input
                  id="simple-edit-qty"
                  type="number"
                  min={1}
                  max={maxAllowedQty}
                  value={newQty}
                  onChange={(e) => setNewQty(e.target.value)}
                  onBlur={() => setTouched(true)}
                  className="mt-1 w-full text-base font-semibold text-secondary-900 tabular-nums leading-none bg-transparent border-0 p-0 focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
              {showDelta && (
                <span
                  className={`inline-flex items-center font-mono text-xs font-semibold px-2 py-1 rounded-md flex-shrink-0 ${
                    delta < 0
                      ? "bg-error-50 text-error-700 border-error-100"
                      : "bg-success-50 text-success-700 border-success-100"
                  }`}
                >
                  {delta > 0 ? "+" : ""}
                  {delta}
                </span>
              )}
            </div>
            {touched && qtyError && <p className="mt-1.5 text-xs text-error-600">{qtyError}</p>}
            <p className="mt-1.5 text-[11px] text-secondary-500">
              Available: {maxAllowedQty} unit{maxAllowedQty !== 1 ? "s" : ""} (received minus what's already in other
              cases for this PO).
            </p>
          </div>

          <div className="grid grid-cols-[7rem_1fr] gap-3">
            <div>
              <label
                htmlFor="simple-edit-date-type"
                className="block text-[11px] font-semibold uppercase tracking-wider text-secondary-500 mb-2"
              >
                Date Type
              </label>
              <div className="relative">
                <select
                  id="simple-edit-date-type"
                  value={dateType}
                  onChange={(e) => setDateType(e.target.value as EuRelabelCheckInProductDateType | "")}
                  onBlur={() => setTouched(true)}
                  className={`w-full appearance-none px-3 py-2.5 pr-9 text-sm bg-white border rounded-lg focus:outline-none focus:ring-1 transition-colors ${
                    touched && datePairError && !hasType
                      ? "border-error-300 focus:border-error-500 focus:ring-error-500"
                      : "border-secondary-300 focus:border-secondary-900 focus:ring-secondary-900"
                  } ${dateType ? "text-secondary-900" : "text-secondary-400"}`}
                >
                  <option value="">—</option>
                  {DATE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
              </div>
            </div>
            <div>
              <label
                htmlFor="simple-edit-date"
                className="block text-[11px] font-semibold uppercase tracking-wider text-secondary-500 mb-2"
              >
                Date
              </label>
              <input
                id="simple-edit-date"
                type="date"
                value={productDate}
                onChange={(e) => setProductDate(e.target.value)}
                onBlur={() => setTouched(true)}
                className={`w-full px-3.5 py-2.5 text-sm text-secondary-900 bg-white border rounded-lg focus:outline-none focus:ring-1 transition-colors ${
                  touched && datePairError && !hasDate
                    ? "border-error-300 focus:border-error-500 focus:ring-error-500"
                    : "border-secondary-300 focus:border-secondary-900 focus:ring-secondary-900"
                }`}
              />
            </div>
          </div>
          {touched && datePairError && <p className="-mt-3 text-xs text-error-600">{datePairError}</p>}

          <div>
            <label
              htmlFor="simple-edit-lot"
              className="block text-[11px] font-semibold uppercase tracking-wider text-secondary-500 mb-2"
            >
              Lot Number
            </label>
            <input
              id="simple-edit-lot"
              type="text"
              value={lotNumber}
              onChange={(e) => setLotNumber(e.target.value)}
              onBlur={() => setTouched(true)}
              maxLength={20}
              placeholder="e.g. LOT-001 (leave blank to clear)"
              className="w-full px-3.5 py-2.5 text-sm text-secondary-900 placeholder:text-secondary-400 bg-white border border-secondary-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-secondary-900 focus:border-secondary-900"
            />
          </div>

          {submitError && (
            <div className="flex items-start gap-2 rounded-lg border border-error-200 bg-error-50 p-3">
              <AlertTriangle className="w-4 h-4 text-error-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error-700">{submitError}</p>
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-3.5 border-t border-secondary-100 bg-secondary-50/40 sm:space-x-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-secondary-900 rounded-lg hover:bg-black transition-colors disabled:bg-secondary-300 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { EditCaseProductModal };
